import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Sparkles,
  Send,
  Heart,
  MessageCircle,
  ExternalLink,
  Loader2,
  Search as SearchIcon,
  Eye,
  BadgeCheck,
  Users,
  Globe,
  Tag,
  ShoppingBag,
  FileText,
  Languages,
  MapPin,
  Copy,
  Check,
  Coins,
  History,
} from "lucide-react";
import { DialogRoot } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChatHistorySidebar } from "./ChatHistorySidebar";
import { TranscriptViewer } from "./TranscriptViewer";
import { formatStructuredTranscriptForCopy } from "./transcriptParser";
import {
  aiSearchService,
  getAiSearchErrorMessage,
  getViewCount,
  type AiSearchMessage,
  type SociableSearchResponse,
  type SociableSearchResult,
  type SociablePostDetailResponse,
} from "@/services/aiSearch.service";

const DEFAULT_PACK = { credits: 50, priceUsd: 50, validityDays: 30 };

const EXAMPLE_QUERIES = [
  "funny love island reactions from the last 90 days",
  "videos of a cat walking around from the last two weeks",
  "top content from USA in the last week",
  "beginner Roth IRA tutorials",
];

const LOADING_STAGES = [
  "Interpreting your query…",
  "Scanning posts across the corpus…",
  "Ranking matches — this usually takes 30–60 seconds…",
  "Still working — refining results…",
];

/** Stop polling a search after this long and treat it as timed out. */
const MAX_POLL_DURATION_MS = 3 * 60_000;

const TIMEOUT_MESSAGE =
  "The search took too long to complete. Please try again.";

type UiMessage =
  | { id: string; role: "user"; text: string }
  | {
      id: string;
      role: "assistant";
      text?: string;
      response?: SociableSearchResponse;
      error?: string;
      isLoading?: boolean;
      elapsedMs?: number;
    };

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${n}`;
}

function formatMetricCount(value: number | null): string {
  return value === null ? "—" : formatCount(value);
}

function formatElapsed(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m <= 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function MetricsRow({
  metrics,
  size = "sm",
}: {
  metrics: SociableSearchResult["metrics"];
  size?: "sm" | "md";
}) {
  const views = getViewCount(metrics);
  const iconClass = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";
  const textClass = size === "md" ? "text-sm" : "text-xs";

  return (
    <div className={`flex items-center gap-3 text-muted-foreground flex-wrap ${textClass}`}>
      <span className="flex items-center gap-1" title="Views">
        <Eye className={iconClass} />
        {formatMetricCount(views)}
      </span>
      <span className="flex items-center gap-1" title="Likes">
        <Heart className={iconClass} />
        {formatCount(metrics.likes)}
      </span>
      <span className="flex items-center gap-1" title="Comments">
        <MessageCircle className={iconClass} />
        {formatCount(metrics.comments)}
      </span>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function ResultCard({
  result,
  onOpen,
}: {
  result: SociableSearchResult;
  onOpen: (result: SociableSearchResult) => void;
}) {
  return (
    <div className="flex flex-col overflow-hidden bg-card border border-border rounded-xl hover:shadow-md transition-shadow">
      <div className="relative bg-black aspect-[9/16] w-full">
        <video
          src={result.mediaUrl}
          className="w-full h-full object-cover"
          controls
          muted
          playsInline
          preload="metadata"
        />
      </div>
      <button
        type="button"
        onClick={() => onOpen(result)}
        className="flex flex-col flex-1 p-3 text-left cursor-pointer hover:bg-accent transition-colors"
        title="View post details"
      >
        <div className="flex items-center justify-between mb-2 w-full">
          <span className="text-xs font-medium text-brand-600 truncate">
            {result.creatorHandle}
          </span>
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            {formatDate(result.postedAt)}
          </span>
        </div>

        {result.subject && (
          <p className="text-xs font-medium text-foreground mb-1 line-clamp-2">
            {result.subject}
          </p>
        )}

        <p className="text-xs text-muted-foreground line-clamp-3 flex-1">
          {result.caption}
        </p>

        <div className="flex items-center justify-between gap-3 mt-3 pt-2 border-t border-border w-full">
          <MetricsRow metrics={result.metrics} />
          <span className="flex items-center gap-1 text-xs font-medium text-brand-600 shrink-0">
            Details
            <ExternalLink className="w-3.5 h-3.5" />
          </span>
        </div>
      </button>
    </div>
  );
}

function LoadingBubble({ elapsedMs = 0 }: { elapsedMs?: number }) {
  const stageIndex = Math.min(
    LOADING_STAGES.length - 1,
    Math.floor(elapsedMs / 15_000)
  );

  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-brand-600" />
      </div>
      <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 space-y-1">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-brand-600" />
          {LOADING_STAGES[stageIndex]}
        </div>
        <p className="text-xs text-muted-foreground pl-6">
          Elapsed {formatElapsed(elapsedMs)}
        </p>
      </div>
    </div>
  );
}

function AssistantMessage({
  message,
  onSuggestionClick,
  onOpenPost,
}: {
  message: UiMessage & { role: "assistant" };
  onSuggestionClick: (q: string) => void;
  onOpenPost: (result: SociableSearchResult) => void;
}) {
  if (message.isLoading) {
    return <LoadingBubble elapsedMs={message.elapsedMs} />;
  }

  if (message.error) {
    return (
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-red-600" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-2xl">
          <p className="text-sm text-red-700">{message.error}</p>
        </div>
      </div>
    );
  }

  const response = message.response;
  if (!response) return null;

  return (
    <div className="flex items-start gap-3 w-full">
      <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-brand-600" />
      </div>
      <div className="flex-1 min-w-0 space-y-4">
        <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 max-w-3xl">
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {response.message}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {response.totalCount} result{response.totalCount === 1 ? "" : "s"}
          </p>
        </div>

        {response.results.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {response.results.map((result) => (
              <ResultCard
                key={result.id}
                result={result}
                onOpen={onOpenPost}
              />
            ))}
          </div>
        )}

        {response.suggestions.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Try instead:</p>
            <div className="flex flex-wrap gap-2">
              {response.suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => onSuggestionClick(s)}
                  className="px-3 py-1.5 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-full hover:bg-brand-100 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-full">
      {children}
    </span>
  );
}

function PostDetailModal({
  result,
  open,
  onClose,
}: {
  result: SociableSearchResult | null;
  open: boolean;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<SociablePostDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcriptCopied, setTranscriptCopied] = useState(false);

  useEffect(() => {
    if (!open || !result) return;

    let cancelled = false;
    setDetail(null);
    setError(null);
    setTranscriptCopied(false);
    setIsLoading(true);

    aiSearchService
      .getPost(result.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(getAiSearchErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, result]);

  if (!result) return null;

  const post = detail?.post;
  const creator = detail?.creator;
  const analysis = detail?.analysis;
  const metrics = post?.metrics ?? result.metrics;
  const mediaUrl = post?.mediaUrl ?? result.mediaUrl;
  const url = post?.url ?? result.url;
  const caption = post?.caption ?? result.caption;

  const handleCopyTranscript = async () => {
    if (!detail?.transcript) return;

    try {
      await navigator.clipboard.writeText(
        formatStructuredTranscriptForCopy(detail.transcript)
      );
      setTranscriptCopied(true);
      toast.success("Transcript copied");
      window.setTimeout(() => setTranscriptCopied(false), 2000);
    } catch {
      toast.error("Could not copy transcript");
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(o) => !o && onClose()}
      className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
        <h2 className="text-base font-semibold text-foreground">Post details</h2>
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            Open on {result.platform || "Instagram"}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      <div className="overflow-y-auto p-5">
        <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-5">
          <div className="w-full">
            <div className="relative bg-black rounded-xl overflow-hidden aspect-[9/16] max-h-[420px] mx-auto">
              <video
                src={mediaUrl}
                className="w-full h-full object-contain"
                controls
                playsInline
                preload="metadata"
              />
            </div>
            <div className="flex items-center justify-center mt-3">
              <MetricsRow metrics={metrics} size="md" />
            </div>
          </div>

          <div className="min-w-0 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-foreground">
                    {creator?.name || result.creatorHandle}
                  </span>
                  {creator?.isVerified && (
                    <BadgeCheck className="w-4 h-4 text-blue-500" />
                  )}
                </div>
                <span className="text-sm text-brand-600">
                  {creator?.handle || result.creatorHandle}
                </span>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatDate(post?.postedAt ?? result.postedAt)}
              </span>
            </div>

            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-brand-600" />
                Loading full details…
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {creator && (
              <div className="flex flex-wrap gap-2">
                <InfoChip>
                  <Users className="w-3.5 h-3.5" />
                  {formatCount(creator.followers)} followers
                </InfoChip>
                {creator.category && <InfoChip>{creator.category}</InfoChip>}
              </div>
            )}

            {creator?.bio && (
              <p className="text-sm text-muted-foreground">{creator.bio}</p>
            )}

            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Caption
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-line">
                {caption}
              </p>
            </div>

            {analysis?.summary && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Summary
                </h3>
                <p className="text-sm text-muted-foreground">{analysis.summary}</p>
              </div>
            )}

            {analysis && (
              <div className="flex flex-wrap gap-2">
                {analysis.format && (
                  <InfoChip>
                    <FileText className="w-3.5 h-3.5" />
                    {analysis.format.category}
                    {analysis.format.subtype
                      ? ` · ${analysis.format.subtype}`
                      : ""}
                  </InfoChip>
                )}
                {analysis.language && (
                  <InfoChip>
                    <Languages className="w-3.5 h-3.5" />
                    {analysis.language.toUpperCase()}
                  </InfoChip>
                )}
                {analysis.regions?.map((r) => (
                  <InfoChip key={r.code}>
                    <MapPin className="w-3.5 h-3.5" />
                    {r.code.replace(/_/g, " ")}
                    {typeof r.confidence === "number"
                      ? ` (${Math.round(r.confidence * 100)}%)`
                      : ""}
                  </InfoChip>
                ))}
              </div>
            )}

            {analysis?.topics && analysis.topics.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  Topics
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.topics.map((t) => (
                    <InfoChip key={t}>
                      <Globe className="w-3.5 h-3.5" />
                      {t.replace(/^Topics >\s*/, "")}
                    </InfoChip>
                  ))}
                </div>
              </div>
            )}

            {analysis?.brands && analysis.brands.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  Brands
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.brands.map((b) => (
                    <InfoChip key={b}>
                      <Tag className="w-3.5 h-3.5" />
                      {b}
                    </InfoChip>
                  ))}
                </div>
              </div>
            )}

            {analysis?.products && analysis.products.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  Products
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.products.map((p, i) => (
                    <InfoChip key={`${p.name}-${i}`}>
                      <ShoppingBag className="w-3.5 h-3.5" />
                      {p.name}
                      {p.brand ? ` · ${p.brand}` : ""}
                    </InfoChip>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {detail?.transcript && (
          <div className="mt-5 pt-5 border-t border-border">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Transcript
              </h3>
              <button
                type="button"
                onClick={handleCopyTranscript}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-lg hover:bg-brand-100 transition-colors"
              >
                {transcriptCopied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy transcript
                  </>
                )}
              </button>
            </div>
            <TranscriptViewer transcript={detail.transcript} />
          </div>
        )}
      </div>
    </DialogRoot>
  );
}

function mapServerMessages(
  messages: AiSearchMessage[],
  pollingSearchId: string | null,
  elapsedMs: number,
  timedOutSearchIds: ReadonlySet<string>
): UiMessage[] {
  return messages.map((m) => {
    if (m.role === "USER") {
      return { id: m.id, role: "user", text: m.text || "" };
    }

    if (m.status === "PENDING" || m.status === "RUNNING") {
      if (timedOutSearchIds.has(m.id)) {
        return {
          id: m.id,
          role: "assistant",
          error: TIMEOUT_MESSAGE,
        };
      }

      return {
        id: m.id,
        role: "assistant",
        isLoading: true,
        elapsedMs: pollingSearchId === m.id ? elapsedMs : 0,
      };
    }

    if (m.status === "FAILED") {
      return {
        id: m.id,
        role: "assistant",
        error: m.errorMessage || "Something went wrong while searching.",
      };
    }

    return {
      id: m.id,
      role: "assistant",
      response: m.resultJson || undefined,
    };
  });
}

/** `modeSwitch` is the Social posts / TikTok creators switch, when creator search is on. */
export default function AiSearch({ modeSwitch }: { modeSwitch?: React.ReactNode } = {}) {
  const queryClient = useQueryClient();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedPost, setSelectedPost] =
    useState<SociableSearchResult | null>(null);
  const [pollingSearchId, setPollingSearchId] = useState<string | null>(null);
  const [confirmPackOpen, setConfirmPackOpen] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [timedOutSearchIds, setTimedOutSearchIds] = useState<Set<string>>(
    () => new Set()
  );
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer with its content: one line when empty, grows to
  // 160px, then scrolls. The empty case is pinned explicitly — a wrapping
  // placeholder inflates scrollHeight and would start the box 2-3 lines tall.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = input ? `${Math.min(el.scrollHeight, 160)}px` : "46px";
  }, [input]);
  const searchStartedAt = useRef<number | null>(null);
  // Search ids we've already seen finish (or time out). Prevents the
  // resume-polling effect from re-arming polling for the same search when the
  // refetched conversation still shows it as PENDING/RUNNING for a moment,
  // which previously caused an infinite invalidate/refetch loop.
  const finishedSearchIds = useRef<Set<string>>(new Set());

  const creditsQuery = useQuery({
    queryKey: ["ai-search", "credits"],
    queryFn: () => aiSearchService.getCredits(),
  });

  const conversationsQuery = useQuery({
    queryKey: ["ai-search", "conversations"],
    queryFn: () => aiSearchService.listConversations(),
  });

  const conversationQuery = useQuery({
    queryKey: ["ai-search", "conversation", activeChatId],
    queryFn: () => aiSearchService.getConversation(activeChatId!),
    enabled: Boolean(activeChatId),
  });

  const searchStatusQuery = useQuery({
    queryKey: ["ai-search", "search", pollingSearchId],
    queryFn: () => aiSearchService.getSearchStatus(pollingSearchId!),
    enabled: Boolean(pollingSearchId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "COMPLETED" || status === "FAILED") return false;
      return 3000;
    },
  });

  const chats = conversationsQuery.data ?? [];
  const credits = creditsQuery.data?.remaining ?? 0;
  const maxCredits = creditsQuery.data?.total ?? 0;
  const planName = creditsQuery.data?.planName;
  const pack = creditsQuery.data?.pack ?? DEFAULT_PACK;
  const chatsLoading = conversationsQuery.isPending;
  const creditsLoading = creditsQuery.isPending;
  const creditsLoaded = Boolean(creditsQuery.data);
  const hasCredits = credits > 0;
  const outOfCredits = creditsLoaded && !hasCredits;

  const serverMessages = conversationQuery.data?.messages ?? [];

  const messages = useMemo(
    () =>
      mapServerMessages(
        serverMessages,
        pollingSearchId,
        elapsedMs,
        timedOutSearchIds
      ),
    [serverMessages, pollingSearchId, elapsedMs, timedOutSearchIds]
  );

  const startSearchMutation = useMutation({
    mutationFn: ({
      query,
      conversationId,
    }: {
      query: string;
      conversationId?: string | null;
    }) => aiSearchService.startSearch(query, conversationId),
    onSuccess: (data) => {
      setActiveChatId(data.conversationId);
      searchStartedAt.current = Date.now();
      setPollingSearchId(data.searchId);
      void queryClient.invalidateQueries({
        queryKey: ["ai-search", "conversations"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["ai-search", "conversation", data.conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ["ai-search", "credits"] });
    },
    onError: (err) => {
      toast.error(getAiSearchErrorMessage(err));
    },
  });

  const purchasePackMutation = useMutation({
    mutationFn: () => aiSearchService.purchasePack(),
    onSuccess: (data) => {
      queryClient.setQueryData(["ai-search", "credits"], data);
      setConfirmPackOpen(false);
      toast.success(
        `Added ${data.pack.credits} searches — billed $${data.pack.priceUsd} to your subscription.`
      );
    },
    onError: (err) => {
      setConfirmPackOpen(false);
      toast.error(getAiSearchErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => aiSearchService.deleteConversation(id),
    onSuccess: (_data, deletedId) => {
      if (activeChatId === deletedId) {
        setActiveChatId(null);
      }
      void queryClient.invalidateQueries({
        queryKey: ["ai-search", "conversations"],
      });
    },
    onError: (err) => {
      toast.error(getAiSearchErrorMessage(err));
    },
  });

  const isSearching = Boolean(pollingSearchId);
  const busy = isSearching || startSearchMutation.isPending;
  const showEmptyState = !busy && messages.length === 0;

  // Scroll only when a message is added or the chat changes — not on every
  // elapsed-timer tick, which made the screen twitch during long searches.
  const messageCount = messages.length;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageCount, activeChatId]);

  useEffect(() => {
    setInput("");
    setSelectedPost(null);
  }, [activeChatId]);

  // Resume polling if a conversation has an in-flight search we haven't
  // already finished or given up on.
  useEffect(() => {
    if (!conversationQuery.data || pollingSearchId) return;
    const inflight = conversationQuery.data.messages.find(
      (m) =>
        m.role === "ASSISTANT" &&
        (m.status === "PENDING" || m.status === "RUNNING") &&
        !finishedSearchIds.current.has(m.id)
    );
    if (!inflight) return;

    const startedAt = new Date(inflight.createdAt).getTime();
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      // Stale search (e.g. server died mid-search) — don't poll it forever.
      finishedSearchIds.current.add(inflight.id);
      setTimedOutSearchIds((prev) => new Set(prev).add(inflight.id));
      return;
    }

    searchStartedAt.current = startedAt;
    setPollingSearchId(inflight.id);
  }, [conversationQuery.data, pollingSearchId]);

  // Live elapsed timer while polling.
  useEffect(() => {
    if (!pollingSearchId) {
      setElapsedMs(0);
      searchStartedAt.current = null;
      return;
    }

    if (!searchStartedAt.current) {
      searchStartedAt.current = Date.now();
    }

    const tick = () => {
      setElapsedMs(Date.now() - (searchStartedAt.current || Date.now()));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [pollingSearchId]);

  // When poll completes, refresh conversation + credits and stop polling.
  useEffect(() => {
    const status = searchStatusQuery.data?.status;
    if (!status || !pollingSearchId) return;
    if (status !== "COMPLETED" && status !== "FAILED") return;

    finishedSearchIds.current.add(pollingSearchId);
    setPollingSearchId(null);
    void queryClient.invalidateQueries({
      queryKey: ["ai-search", "conversation", activeChatId],
    });
    void queryClient.invalidateQueries({
      queryKey: ["ai-search", "conversations"],
    });
    void queryClient.invalidateQueries({ queryKey: ["ai-search", "credits"] });
  }, [
    searchStatusQuery.data?.status,
    pollingSearchId,
    activeChatId,
    queryClient,
  ]);

  // Give up on searches that run past the cap so polling can't go forever.
  useEffect(() => {
    if (!pollingSearchId || elapsedMs < MAX_POLL_DURATION_MS) return;

    const searchId = pollingSearchId;
    finishedSearchIds.current.add(searchId);
    setTimedOutSearchIds((prev) => new Set(prev).add(searchId));
    setPollingSearchId(null);
    toast.error(TIMEOUT_MESSAGE);
  }, [pollingSearchId, elapsedMs]);

  const handleSelectChat = (chatId: string) => {
    if (isSearching) return;
    setActiveChatId(chatId);
    setHistoryOpen(false);
  };

  const handleNewChat = () => {
    if (isSearching) return;
    setActiveChatId(null);
    setHistoryOpen(false);
  };

  const handleDeleteChat = (chatId: string) => {
    if (isSearching) return;
    deleteMutation.mutate(chatId);
  };

  const runSearch = useCallback(
    (rawQuery: string) => {
      const query = rawQuery.trim();
      if (!query || isSearching || startSearchMutation.isPending) return;
      if (query.length < 2) return;
      if (!hasCredits) {
        toast.error(
          `You have used all your Social searches for this month. Buy ${pack.credits} more for $${pack.priceUsd}.`
        );
        return;
      }

      setInput("");
      startSearchMutation.mutate({
        query,
        conversationId: activeChatId,
      });
    },
    [isSearching, startSearchMutation, hasCredits, activeChatId, pack]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(input);
  };

  return (
    <div className="flex h-[calc(100vh-1rem)] md:h-[calc(100vh-4rem)] w-full">
      <ChatHistorySidebar
        chats={chats}
        activeChatId={activeChatId}
        credits={credits}
        maxCredits={maxCredits}
        hasCredits={hasCredits}
        planName={planName}
        chatsLoading={chatsLoading}
        creditsLoading={creditsLoading}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        disabled={busy}
        className="hidden md:flex w-64"
      />

      {historyOpen && (
        <>
          <button
            type="button"
            aria-label="Close chat history"
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setHistoryOpen(false)}
          />
          <ChatHistorySidebar
            chats={chats}
            activeChatId={activeChatId}
            credits={credits}
            maxCredits={maxCredits}
            hasCredits={hasCredits}
            planName={planName}
            chatsLoading={chatsLoading}
            creditsLoading={creditsLoading}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            disabled={busy}
            className="fixed left-0 top-0 bottom-0 z-50 w-72 md:hidden shadow-xl"
          />
        </>
      )}

      <div className="flex flex-col flex-1 min-w-0">
        <div className="px-4 md:px-6 pt-2 pb-4 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-brand-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-brand-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">Social Search</h1>
              {/* On phones the description drops below the icon row so it
                  isn't squeezed into the leftover width beside two icons */}
              <p className="hidden sm:block text-muted-foreground text-sm">
                Search short-form social content with natural language.
              </p>
            </div>
            {/* Right-aligned so it never sits under the layout's fixed
                hamburger at the top-left */}
            <button
              type="button"
              onClick={() => setHistoryOpen(true)}
              className="md:hidden shrink-0 p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
              aria-label="Open chat history"
              title="Chat history"
            >
              <History className="w-5 h-5" />
            </button>
          </div>
          <p className="sm:hidden mt-1 text-muted-foreground text-sm">
            Search short-form social content with natural language.
          </p>
          {modeSwitch && <div className="mt-3">{modeSwitch}</div>}
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-6">
          {showEmptyState ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-xl mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center mb-4">
                <SearchIcon className="w-7 h-7 text-brand-600" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                What are you looking for?
              </h2>
              <p className="text-sm text-muted-foreground mt-1 mb-6">
                Describe a topic, format, audience, time range, or location in
                plain English. Searches typically take 30–60 seconds.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLE_QUERIES.map((q) => (
                  <button
                    key={q}
                    onClick={() => runSearch(q)}
                    disabled={!hasCredits || busy || creditsLoading}
                    className="px-3 py-2 text-xs font-medium text-muted-foreground bg-card border border-border rounded-full hover:border-brand-300 hover:text-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6 pb-6">
              {messages.map((message) =>
                message.role === "user" ? (
                  <div key={message.id} className="flex justify-end">
                    <div className="bg-brand-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-2xl">
                      <p className="text-sm">{message.text}</p>
                    </div>
                  </div>
                ) : (
                  <AssistantMessage
                    key={message.id}
                    message={message}
                    onSuggestionClick={runSearch}
                    onOpenPost={setSelectedPost}
                  />
                )
              )}
              {busy && messages.every((m) => m.role !== "assistant" || !("isLoading" in m && m.isLoading)) && (
                <LoadingBubble elapsedMs={elapsedMs} />
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="shrink-0 px-4 md:px-6 py-4 border-t border-border bg-card/60 backdrop-blur">
          {outOfCredits && (
            <div className="max-w-3xl mx-auto mb-3 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  You&apos;re out of Social searches for this month
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Buy {pack.credits} more searches for ${pack.priceUsd}, billed
                  to your Shopify subscription. Pack searches are valid for{" "}
                  {pack.validityDays} days from purchase.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => setConfirmPackOpen(true)}
                disabled={purchasePackMutation.isPending}
                className="shrink-0"
              >
                {purchasePackMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Coins className="w-4 h-4" />
                )}
                Buy {pack.credits} searches — ${pack.priceUsd}
              </Button>
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="flex items-end gap-2 max-w-3xl mx-auto"
          >
            <textarea
              ref={composerRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              rows={1}
              placeholder={
                creditsLoading
                  ? "Loading your search credits…"
                  : hasCredits
                    ? "Search for content…"
                    : "You're out of searches for this month."
              }
              disabled={busy || creditsLoading || !hasCredits}
              className="flex-1 min-w-0 resize-none overflow-y-auto min-h-[46px] max-h-40 rounded-xl border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-muted disabled:text-muted-foreground"
            />
            <Button
              type="submit"
              size="icon"
              disabled={
                busy ||
                creditsLoading ||
                input.trim().length < 2 ||
                !hasCredits
              }
              className="h-10 w-10 shrink-0 self-end"
              aria-label="Search"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </div>

        <PostDetailModal
          result={selectedPost}
          open={selectedPost !== null}
          onClose={() => setSelectedPost(null)}
        />

        <DialogRoot
          open={confirmPackOpen}
          onOpenChange={(open) => {
            if (!purchasePackMutation.isPending) setConfirmPackOpen(open);
          }}
          className="max-w-sm"
        >
          <div className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5 text-brand-600" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Buy {pack.credits} Social searches for ${pack.priceUsd}?
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              A one-time ${pack.priceUsd} usage charge will be added to your
              Shopify subscription immediately. The {pack.credits} searches are
              valid for {pack.validityDays} days from purchase.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmPackOpen(false)}
                disabled={purchasePackMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => purchasePackMutation.mutate()}
                disabled={purchasePackMutation.isPending}
              >
                {purchasePackMutation.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {purchasePackMutation.isPending
                  ? "Processing…"
                  : `Confirm — $${pack.priceUsd}`}
              </Button>
            </div>
          </div>
        </DialogRoot>
      </div>
    </div>
  );
}
